// DealCardImage.tsx
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  CSSProperties
} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Box, CardMedia } from '@mui/material';
import config from '@configFile';
import { getProductLink, getPostPrice } from '@utils/index';
import MonetizingLink from '@components/MonetizingLink';
import { DealPostCardProps } from '@components/DealCard/utils';

const randomDelayValues = [2000, 2150, 2300, 2500, 2800, 3000];
const httpsify = (u?: string) => (u ? u.replace(/^http:/, 'https:') : u);

const DealCardImage = (dealProps: DealPostCardProps): JSX.Element => {
  const {
    previewMode,
    ASIN,
    image240Url,
    image240Height,
    image240Width,
    image600Url,
    image,
    shouldLazyLoad,
    tag = config.AFFILIATE_TAGS.DEFAULT,
    // Ensure your type includes this in DealPostCardProps
    haulImage,
    haulImageHeight,
    haulImageWidth
  } = dealProps;

  const link = getProductLink(ASIN, tag);

  // Prefer webp if available
  const image240UrlWebp = image240Url?.replace('.jpg', '.webp');
  const image600UrlWebp = image600Url?.replace('.jpg', '.webp');
  const primary = httpsify(image240UrlWebp || image600UrlWebp || image);
  const secondary = httpsify(haulImage);

  // Target render box: keep your existing scale (0.8x 240 dims)
  const targetHeight = Math.round((image240Height || 240) * 0.8);
  const primaryWidthAtHeight = Math.round((image240Width || 240) * 0.8);

  const hasCarousel = Boolean(primary && secondary && secondary !== primary);

  // Dynamically compute viewport width so both slides can be height-locked without crop
  const [boxWidth, setBoxWidth] = useState<number>(primaryWidthAtHeight);

  useEffect(() => {
    if (!hasCarousel || !secondary) {
      setBoxWidth(primaryWidthAtHeight);
      return;
    }
    const img = new Image();
    img.src = secondary;
    const handle = () => {
      const secW =
        img.naturalHeight > 0
          ? Math.round((img.naturalWidth / img.naturalHeight) * targetHeight)
          : primaryWidthAtHeight;
      setBoxWidth(Math.max(primaryWidthAtHeight, secW));
    };
    img.onload = handle;
    img.onerror = () => setBoxWidth(primaryWidthAtHeight);
    // eslint-disable-next-line consistent-return
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [hasCarousel, secondary, primaryWidthAtHeight, targetHeight]);

  // Embla (only when carousel)
  const autoplay = useRef(
    Autoplay({
      delay:
        randomDelayValues[Math.floor(Math.random() * randomDelayValues.length)],
      stopOnInteraction: false,
      stopOnMouseEnter: false
    })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', startIndex: 0 },
    [autoplay.current]
  );

  useEffect(() => {
    if (!emblaApi || !hasCarousel) return;
    emblaApi.scrollTo(0, true);
  }, [emblaApi, hasCarousel]);

  const imgLoading = shouldLazyLoad ? 'lazy' : 'eager';

  const extraStyle = useMemo<CSSProperties>(() => {
    const style: CSSProperties = {};
    if (!haulImageHeight || !haulImageWidth) return style;
    if (haulImageHeight > 139) {
      style.height = targetHeight;
    } else if (haulImageWidth > 139) {
      style.width = haulImageWidth;
    }
    return style;
  }, [haulImageHeight, haulImageWidth, targetHeight, secondary]);

  return (
    <MonetizingLink
      href={link}
      sx={{ display: 'block' }}
      tag={tag}
      clickType="deal-post-card-image"
      aria-label={`View deal details${getPostPrice(dealProps)} at Amazon.com`}
    >
      {hasCarousel ? (
        <Box
          ref={emblaRef}
          sx={{
            overflow: 'hidden', // Embla viewport
            margin: '0px auto 16px auto',
            width: `${boxWidth}px`,
            height: `${targetHeight}px`,
            '@container (min-width: 700px)': {
              width: `${boxWidth}px`,
              height: `${targetHeight}px`
            }
          }}
        >
          <Box sx={{ display: 'flex', touchAction: 'pan-y' }}>
            {[primary!, secondary!].map((src) => (
              <Box
                key={src}
                sx={{
                  position: 'relative',
                  flex: '0 0 100%',
                  minWidth: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box
                  component="img"
                  alt="" // decorative; link has aria-label
                  data-testid="deal-card-media-image"
                  className="deal-card-media-image"
                  src={src}
                  loading={imgLoading}
                  sx={{
                    display: 'block',
                    height: '100%', // lock equal visual height
                    width: 'auto', // preserve aspect ratio
                    maxWidth: '100%', // never overflow viewport width
                    margin: '0 auto',
                    filter: previewMode ? 'blur(8px)' : 'none',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    ...(src === secondary
                      ? extraStyle
                      : {
                          maxHeight: `${targetHeight}px`
                        })
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <CardMedia
          component="img"
          alt=""
          data-testid="deal-card-media-image"
          className="deal-card-media-image"
          image={primary}
          loading={imgLoading}
          sx={{
            width: `${primaryWidthAtHeight}px`,
            height: `${targetHeight}px`,
            maxWidth: `${primaryWidthAtHeight}px!important`,
            maxHeight: `${targetHeight}px!important`,
            display: 'block',
            margin: '0px auto 16px auto',
            textAlign: 'center',
            objectFit: 'contain',
            filter: previewMode ? 'blur(8px)' : 'none',
            '@container (min-width: 700px)': {
              maxWidth: `${primaryWidthAtHeight}px!important`,
              maxHeight: `${targetHeight}px!important`,
              width: `${primaryWidthAtHeight}px`,
              height: `${targetHeight}px`
            }
          }}
        />
      )}
    </MonetizingLink>
  );
};

export default DealCardImage;
